/** BCP 47 tags for `Intl`. Our internal locale codes are the short forms. */
const INTL_LOCALE = { fr: 'fr-FR', en: 'en-GB' };
/**
 * `Intl` formatter construction is comparatively expensive and these are called
 * once per row in a gallery of thousands, so each one is built once and reused.
 */
const cache = new Map();
function cached(key, build) {
    const existing = cache.get(key);
    if (existing)
        return existing;
    const created = build();
    cache.set(key, created);
    return created;
}
export function formatDate(value, locale) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return cached(`d:${locale}`, () => new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'long' })).format(date);
}
export function formatDateTime(value, locale) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return cached(`dt:${locale}`, () => new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'medium', timeStyle: 'short' })).format(date);
}
/** The time of day alone, e.g. `14:32`. */
export function formatTime(value, locale) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return cached(`t:${locale}`, () => new Intl.DateTimeFormat(INTL_LOCALE[locale], { timeStyle: 'short' })).format(date);
}
/**
 * A date that is a **wall clock**, not an instant, formatted without converting it.
 *
 * EXIF records the time a camera's own clock showed and usually omits the
 * offset entirely, so there is nothing to convert *from*. Those timestamps are
 * stored as if they were UTC precisely so the digits survive, and rendering one
 * through {@link formatDate} would undo that: a photo taken at 23:30 moves to
 * the next day for a reader two timezones east, and the gallery files it under
 * a heading the person who took it would not recognise.
 *
 * Pinning `timeZone: 'UTC'` is what "displayed without conversion" means in
 * practice - the value went in as UTC-shaped digits and comes out as the same
 * digits, wherever it is read.
 *
 * Use it for `takenAt` and for anything else derived from a camera's clock. Use
 * {@link formatDate} for a real instant such as `createdAt`, which is a moment
 * this server observed and does deserve converting.
 */
export function formatWallClockDate(value, locale) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return cached(`wd:${locale}`, () => new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'long', timeZone: 'UTC' })).format(date);
}
/**
 * A span of wall-clock days, written the way the locale writes a range.
 *
 * `Intl`'s own `formatRange` rather than two formatted dates with a dash
 * between them, because a range is not a concatenation: English wants
 * "12–15 August 2026" and French "12 – 15 août 2026", the shared month and year
 * are collapsed only when they are actually shared, and a span crossing a month
 * or a year opens them back up. Written by hand, that is a table of cases per
 * language; here it is the platform's job.
 *
 * It also collapses a range whose ends fall on the same day to a single date,
 * which is exactly right for an outing that lasted an afternoon.
 *
 * Pinned to UTC for the reason {@link formatWallClockDate} gives: these come
 * from a camera's clock, and converting them would move a trip that ended at
 * 23:30 onto the following day.
 */
export function formatWallClockDateRange(start, end, locale) {
    const from = typeof start === 'string' ? new Date(start) : start;
    const to = typeof end === 'string' ? new Date(end) : end;
    return cached(`wdr:${locale}`, () => new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'long', timeZone: 'UTC' })).formatRange(from, to);
}
/**
 * The calendar day a wall-clock timestamp falls on, as `YYYY-MM-DD`.
 *
 * What the gallery groups its date headings by. Derived from the UTC parts for
 * the reason {@link formatWallClockDate} gives, and a sortable key rather than
 * a formatted string so grouping never depends on how a locale writes a date.
 */
export function wallClockDayKey(value) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}
/**
 * The calendar day an **instant** falls on for whoever is reading, as `YYYY-MM-DD`.
 *
 * The counterpart to {@link wallClockDayKey}, and the two are not
 * interchangeable. A timestamp the server observed - an upload time, a sign-in
 * - is a real moment, and the day it belongs to is the reader's own: an upload
 * at 00:30 in Paris is 22:30 the day before in UTC, and filing it under
 * yesterday would be wrong for the person who made it.
 *
 * Built from the local parts rather than from a formatted string, so grouping
 * never depends on how a locale writes a date.
 */
export function localDayKey(value) {
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime()))
        return '';
    return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/**
 * When something happened, at the precision a reader actually needs.
 *
 * The time alone for today and the date as well for anything older, which is the
 * rule a transcript wants and neither of the two obvious alternatives gets right.
 * A full date on every line of a conversation held this afternoon is noise
 * repeated forty times; a bare `14:32` on a message from last week is a lie by
 * omission.
 *
 * **Not relative**, unlike the conversation list. "Three minutes ago" is the
 * right answer to "how recent is this thread" and the wrong one inside the thread
 * itself, where the reader is placing turns against each other and against the
 * clock - and where a label that silently goes stale as they read is worse than
 * useless.
 *
 * `now` is a parameter for the same reason it is on {@link formatRelativeTime}: a
 * function that reads the clock itself can only be tested by mocking time.
 */
export function formatMoment(value, locale, now = new Date()) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return isSameDay(date, now) ? formatTime(date, locale) : formatDateTime(date, locale);
}
export function formatNumber(value, locale) {
    return cached(`n:${locale}`, () => new Intl.NumberFormat(INTL_LOCALE[locale])).format(value);
}
const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB'];
/**
 * Human-readable file size.
 *
 * Uses decimal units (1 kB = 1000 B) to match what the operating system and
 * the storage bill both say, rather than binary units that would make a
 * "5 GB" quota look wrong to the person who filled it.
 */
export function formatBytes(bytes, locale) {
    if (!Number.isFinite(bytes) || bytes < 0)
        return '—';
    if (bytes < 1000)
        return `${String(Math.round(bytes))} ${BYTE_UNITS[0] ?? 'B'}`;
    let value = bytes;
    let unit = 0;
    while (value >= 1000 && unit < BYTE_UNITS.length - 1) {
        value /= 1000;
        unit += 1;
    }
    // One decimal below 10 (9.4 GB), none above (94 GB) - enough precision to be
    // useful without implying accuracy that is not there.
    const digits = value < 10 ? 1 : 0;
    const formatted = cached(`b:${locale}:${String(digits)}`, () => new Intl.NumberFormat(INTL_LOCALE[locale], { minimumFractionDigits: digits, maximumFractionDigits: digits })).format(value);
    return `${formatted} ${BYTE_UNITS[unit] ?? 'TB'}`;
}
const COMPACT_UNITS = [
    [1_000_000, 'M'],
    [1_000, 'K'],
];
/**
 * A large round count, short enough to sit inside a `<select>` option.
 *
 * Hand-rolled rather than `Intl`'s own `notation: 'compact'`, for the same reason
 * {@link formatBytes} carries its own units: `en-GB` compacts a million to `1m`,
 * which in a line about context windows reads as a unit of length or of time
 * rather than as a magnitude. `K` and `M` are what every model's own
 * documentation writes, in either language.
 *
 * The grouping and the decimal mark still come from the locale, so a French
 * reader gets `1,8 M` and an English one `1.8M`.
 */
export function formatCompactNumber(value, locale) {
    if (!Number.isFinite(value))
        return '—';
    const unit = COMPACT_UNITS.find(([divisor]) => Math.abs(value) >= divisor);
    if (!unit)
        return formatNumber(Math.round(value), locale);
    const scaled = value / unit[0];
    // One decimal below ten, none above - the rule `formatBytes` uses, and the one
    // that keeps 1_048_576 reading as the `1M` its provider advertises instead of
    // as a spuriously precise `1.05M`.
    const digits = Math.abs(scaled) < 10 ? 1 : 0;
    const formatted = cached(`c:${locale}:${String(digits)}`, () => new Intl.NumberFormat(INTL_LOCALE[locale], { minimumFractionDigits: 0, maximumFractionDigits: digits })).format(scaled);
    return `${formatted}${unit[1]}`;
}
/**
 * A fraction as a whole percentage - `0.86` becomes `86%`, or `86 %` in French,
 * which puts a narrow no-break space before the sign.
 *
 * Takes the fraction rather than the number of percent so the rounding happens in
 * one place, and deliberately does **not** clamp: a prompt measured at more than
 * its model's advertised window is a real thing to see, and `104%` says so where
 * a capped `100%` would hide it.
 */
export function formatPercent(fraction, locale) {
    if (!Number.isFinite(fraction))
        return '—';
    return cached(`p:${locale}`, () => new Intl.NumberFormat(INTL_LOCALE[locale], { style: 'percent', maximumFractionDigits: 0 })).format(fraction);
}
const RELATIVE_STEPS = [
    [60, 1, 'second'],
    [3600, 60, 'minute'],
    [86400, 3600, 'hour'],
    [604800, 86400, 'day'],
    [2629800, 604800, 'week'],
    [31557600, 2629800, 'month'],
    [Number.POSITIVE_INFINITY, 31557600, 'year'],
];
/** "il y a 3 heures" / "3 hours ago". Used for session lists and photo dates. */
export function formatRelativeTime(value, locale, now = new Date()) {
    const date = typeof value === 'string' ? new Date(value) : value;
    const seconds = (date.getTime() - now.getTime()) / 1000;
    const magnitude = Math.abs(seconds);
    const step = RELATIVE_STEPS.find(([limit]) => magnitude < limit) ?? RELATIVE_STEPS[RELATIVE_STEPS.length - 1];
    if (!step)
        return formatDate(date, locale);
    const formatter = cached(`r:${locale}`, () => new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' }));
    return formatter.format(Math.round(seconds / step[1]), step[2]);
}
/** Test-only: drop memoised formatters so a locale change is observable. */
export function clearFormatterCache() {
    cache.clear();
}
//# sourceMappingURL=formatters.js.map